import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  hexToHsva,
  hsvaToHex,
  hsvaToRgba,
  rgbaToHsva,
} from "@uiw/color-convert";
import { useEffect, useMemo, useState } from "react";
import {
  createAssetApiAssetsPostMutation,
  listProjectAssetsApiProjectsProjectIdAssetsGetOptions,
} from "../generated/api/@tanstack/react-query.gen";
import {
  buildAssetCreateBody,
  clampByte,
  formatColorPickerGroupName,
  isColorPickerOkDisabled,
  parseHexInput,
  seedHsva,
} from "../lib/color-picker";
import type { SelectedColorAsset } from "../lib/parameter-asset";
import { formatParameterLabel } from "../lib/parameter-label";
import { invalidateAssets } from "../lib/queries";
import { useModalCommitGuard } from "./useModalCommitGuard";

/** Props of the color view inside the parameter asset modal. */
export interface ColorPickerProps {
  isOpen: boolean;
  initialHex: string;
  paramKey?: string;
  /** Parameter group heading (e.g. "Upholstery") — shown in the subtitle for context. */
  groupName?: string;
  /**
   * Commit the chosen color. When the user also saved it via "Save as asset",
   * `asset` carries its id + name so callers can show the name and record
   * usage. Omitted for a color that was only picked.
   */
  onSave: (hex: string, asset?: SelectedColorAsset) => void;
  onClose: () => void;
}

export interface ColorPickerStateProps {
  isOpen: boolean;
  initialHex: string;
  paramKey?: string;
}

/**
 * Ids of a project's assets, as returned by the project-assets endpoint — the
 * union of manually attached assets and those derived from the project's
 * designs (not strictly the manually attached ones). Used by the refinement
 * color picker to float a project's own assets to the top of the Assets tab.
 * Returns an empty set when no project is in scope (e.g. refining outside a
 * project).
 */
export function useProjectAssetIds(
  projectId: string | null | undefined,
  enabled: boolean,
): ReadonlySet<string> {
  const query = useQuery({
    ...listProjectAssetsApiProjectsProjectIdAssetsGetOptions({
      path: { project_id: projectId ?? "" },
    }),
    enabled: enabled && Boolean(projectId),
  });
  return useMemo(
    () => new Set((query.data ?? []).map((asset) => asset.id)),
    [query.data],
  );
}

export interface ColorAssetFormState {
  saveAsAsset: boolean;
  setSaveAsAsset: (v: boolean) => void;
  name: string;
  setName: (v: string) => void;
}

export function useColorPickerModalState({
  isOpen,
  initialHex,
  paramKey = "",
}: ColorPickerStateProps) {
  const [hsva, setHsva] = useState(() => seedHsva(initialHex));
  const [hexDraft, setHexDraft] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setHsva(seedHsva(initialHex));
      setHexDraft(null);
    }
  }, [isOpen, initialHex]);

  const rgba = hsvaToRgba(hsva);
  const displayHex = (hexDraft ?? hsvaToHex(hsva)).toUpperCase();

  const applyHexString = (raw: string) => {
    const parsed = parseHexInput(raw);
    if (parsed) {
      setHsva({ ...hexToHsva(parsed), a: 1 });
    }
  };

  const onRgbChange = (channel: "r" | "g" | "b", value: string) => {
    const parsed = Number.parseInt(value, 10);
    if (Number.isNaN(parsed)) return;
    const next = {
      r: channel === "r" ? clampByte(parsed) : rgba.r,
      g: channel === "g" ? clampByte(parsed) : rgba.g,
      b: channel === "b" ? clampByte(parsed) : rgba.b,
      a: 1 as const,
    };
    setHsva(rgbaToHsva(next));
  };

  return {
    hsva,
    setHsva,
    hexDraft,
    setHexDraft,
    formattedParam: formatParameterLabel(paramKey),
    displayHex,
    rgba,
    applyHexString,
    onRgbChange,
    selectedHex: hsvaToHex(hsva).toLowerCase(),
  };
}

export type ColorPickerModalState = ReturnType<typeof useColorPickerModalState>;

export function useColorAssetForm(
  isOpen: boolean,
  alwaysSaveAsAsset = false,
): ColorAssetFormState {
  const [saveAsAsset, setSaveAsAsset] = useState(alwaysSaveAsAsset);
  const [name, setName] = useState("");

  useEffect(() => {
    if (isOpen) {
      setSaveAsAsset(alwaysSaveAsAsset);
      setName("");
    }
  }, [isOpen, alwaysSaveAsAsset]);

  return {
    saveAsAsset,
    setSaveAsAsset,
    name,
    setName,
  };
}

export function useColorPickerSave(
  props: Pick<
    ColorPickerProps,
    "isOpen" | "onSave" | "onClose" | "groupName"
  > & { isColorTab: boolean },
  selectedHex: string,
  assetForm: ColorAssetFormState,
) {
  const { isOpen, isColorTab, onSave, onClose, groupName } = props;
  const queryClient = useQueryClient();
  const guard = useModalCommitGuard({ isOpen, isActiveTab: isColorTab });
  const { mutate, reset, isPending, isError } = useMutation({
    ...createAssetApiAssetsPostMutation(),
    onSettled: () => invalidateAssets(queryClient),
  });

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const formattedGroup = formatColorPickerGroupName(groupName);
  const trimmedName = assetForm.name.trim();
  const wantsToSave = assetForm.saveAsAsset;
  const okDisabled = isColorPickerOkDisabled(
    isPending,
    wantsToSave,
    trimmedName,
  );

  const handleOk = () => {
    if (!wantsToSave) {
      onSave(selectedHex);
      onClose();
      return;
    }
    guard.beginAttempt();
    mutate(
      {
        body: buildAssetCreateBody(trimmedName, selectedHex),
      },
      {
        onSuccess: (created) => {
          if (!guard.shouldCommit()) return;
          onSave(selectedHex, { id: created.id, name: created.name });
          onClose();
        },
      },
    );
  };

  return {
    isError: isError && wantsToSave,
    isPending,
    formattedGroup,
    okDisabled,
    handleOk,
    okLabel: isPending ? "Saving…" : "Ok",
  };
}
